import type { ReportMedia, ReportMovement, ReportSummary } from '@obras/shared';
import {
  formatCoordinate,
  formatCurrency,
  formatCurrencyDelta,
  formatDate,
  formatDateTime,
  formatDuration,
  REPORT_TYPE_META,
} from '@obras/shared';
import * as Icons from 'lucide-react';
import { FileText, Play } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { useState } from 'react';

import { EASE_OUT_QUINT } from '../../components/motion.js';
import { Badge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { Avatar, EmptyState, Skeleton } from '../../components/ui/Feedback.js';
import { Lightbox, useLightbox, type LightboxItem } from '../../components/ui/Lightbox.js';
import { fimDaMoldura, Moldura } from '../../components/ui/Moldura.js';
import { cn } from '../../lib/cn.js';
import { LinhaDeArquivo } from '../documentos/Arquivo.js';
import { VideoEmEspera } from './VideoEstado.js';

const ICON = { strokeWidth: 1.5 } as const;

/** Fundo do marcador na cor da movimentação — mapa fixo de design.md §3. */
const MARKER_TONE: Record<string, string> = {
  muted: 'bg-ink-3',
  success: 'bg-success',
  info: 'bg-info',
  danger: 'bg-danger',
  warning: 'bg-warning',
};

/*
 * A linha do tempo se desenha de cima para baixo: cada marcador ganha a
 * moldura no seu passo da fila, e o fio que desce dele parte da borda de baixo
 * quando a moldura fecha. A fila para no oitavo (design.md §10): do nono em
 * diante, todos desenham juntos.
 */
const PASSO_DA_FILA = 0.06;
const FIM_DA_FILA = 8;
const FIO_DESCE = 0.35;

const atrasoDo = (posicao: number): number => Math.min(posicao, FIM_DA_FILA) * PASSO_DA_FILA;

/** O fio entre um marcador e o seguinte: 1px, no tom do filete da moldura. */
const FIO = 'w-px bg-ink-3/35';

const DELTA_TONE: Record<string, string> = {
  muted: 'text-ink-3',
  success: 'text-success',
  info: 'text-info',
  danger: 'text-danger',
  warning: 'text-warning',
};

function MovementStrip({ movement }: { movement: ReportMovement }) {
  const meta = REPORT_TYPE_META[movement.type];
  const reversed = movement.reversedByMovementId !== null;

  const isTermChange = movement.days !== null;
  const isPayment = meta.effect === 'paid';

  const previous = isTermChange
    ? formatDate(movement.previousEndDate)
    : formatCurrency(isPayment ? movement.previousPaidCents : movement.previousValueCents);

  const next = isTermChange
    ? formatDate(movement.newEndDate)
    : formatCurrency(isPayment ? movement.newPaidCents : movement.newValueCents);

  const delta = isTermChange
    ? `+${movement.days} dia(s)`
    : formatCurrencyDelta(movement.amountCents);

  return (
    <div
      className={cn(
        'tabular mt-3 flex flex-wrap items-center gap-2 bg-sunken px-3 py-2 text-[13px]',
        reversed && 'opacity-70',
      )}
    >
      <span className={cn('flex items-center gap-1.5 font-medium', DELTA_TONE[meta.tone])}>
        {meta.label}
      </span>

      <span className={cn('text-ink-3', reversed && 'line-through')}>{previous}</span>
      <span className="text-ink-3" aria-hidden>
        →
      </span>
      <span className={cn('font-medium', reversed && 'text-ink-3 line-through')}>{next}</span>
      <span className={cn('font-medium', DELTA_TONE[meta.tone], reversed && 'line-through')}>
        {delta}
      </span>

      {reversed ? (
        <Badge tone="muted" icon={Icons.Undo2} className="ml-auto">
          Estornado
        </Badge>
      ) : null}
    </div>
  );
}

function TypeMarker({ type }: { type: ReportSummary['type'] }) {
  const meta = REPORT_TYPE_META[type];
  const Component =
    (Icons as unknown as Record<string, Icons.LucideIcon>)[meta.icon] ?? Icons.FileText;

  return (
    <span
      className={cn(
        'relative z-10 grid size-7 shrink-0 place-items-center',
        MARKER_TONE[meta.tone] ?? 'bg-ink-3',
      )}
    >
      <Component {...ICON} size={14} className="text-on-tone" aria-hidden />
    </span>
  );
}

/**
 * Anexos da visita.
 *
 * Todas as fotos aparecem — o corte em seis com um "+N" mudo escondia
 * justamente o registro de campo que o relatório existe para carregar.
 * Documentos ficam separados: PDF se abre, foto e vídeo se olham.
 *
 * Vídeo e foto dividem a mesma grade e o mesmo visualizador. Separá-los faria
 * a pessoa ver metade da visita numa lista e metade em outra, quando o que ela
 * quer é percorrer o que foi registrado, na ordem em que foi registrado.
 */
/**
 * Uma miniatura da grade.
 *
 * O vídeo tem três estados e nenhum deles pode virar um quadrado cinza mudo:
 * processando (a capa ainda não existe), pronto (capa + duração + play) e
 * falhou (o Bunny recusou o arquivo).
 *
 * Quadrada e sem zoom: sob o ponteiro, a barra laranja cresce do meio no pé do
 * quadro, o gesto do cartão de obra. A foto aberta cresce do quadro até o
 * centro da tela — o quadro manda o próprio retângulo.
 */
function MediaThumb({
  item,
  onOpen,
  label,
}: {
  item: ReportMedia;
  onOpen: (origem: DOMRect) => void;
  label: string;
}) {
  const video = item.kind === 'video';

  if (video && item.status && item.status !== 'pronto') {
    return <VideoEmEspera mediaId={item.id} status={item.status} compacto />;
  }

  return (
    <button
      type="button"
      onClick={(event) => onOpen(event.currentTarget.getBoundingClientRect())}
      aria-label={label}
      className="group relative block aspect-square w-full overflow-hidden bg-sunken"
    >
      <img
        src={video ? item.thumbnailUrl : item.url}
        alt=""
        loading="lazy"
        decoding="async"
        className="size-full object-cover"
      />

      {video ? (
        <>
          <span className="absolute inset-0 grid place-items-center">
            <span className="grid size-9 place-items-center bg-black/55 text-white">
              <Play size={15} strokeWidth={2} fill="currentColor" aria-hidden />
            </span>
          </span>
          {item.durationSeconds ? (
            <span className="tabular absolute right-1 bottom-1 bg-black/65 px-1 py-0.5 text-[10px] font-medium text-white">
              {formatDuration(item.durationSeconds)}
            </span>
          ) : null}
        </>
      ) : null}

      <span
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-0.5 origin-center scale-x-0 bg-primary transition-transform duration-300 ease-out-quint group-hover:scale-x-100 group-focus-visible:scale-x-100 motion-reduce:transition-none"
      />
    </button>
  );
}

function ReportAttachments({ report }: { report: ReportSummary }) {
  const lightbox = useLightbox();
  const [origem, setOrigem] = useState<DOMRect | null>(null);

  const visuais = report.media.filter((item) => item.kind === 'imagem' || item.kind === 'video');
  const documents = report.media.filter((item) => item.kind === 'arquivo');

  const slides: LightboxItem[] = visuais.map((item) => ({
    id: item.id,
    url: item.url,
    title: report.title,
    kind: item.kind === 'video' ? 'video' : 'imagem',
    thumbnailUrl: item.thumbnailUrl,
    meta: [
      formatDateTime(item.takenAt) !== '—'
        ? formatDateTime(item.takenAt)
        : formatDate(report.visitDate),
      item.durationSeconds ? formatDuration(item.durationSeconds) : null,
      item.latitude != null && item.longitude != null
        ? `${formatCoordinate(item.latitude, 'lat')} ${formatCoordinate(item.longitude, 'lng')}`
        : null,
      report.author.name,
    ].filter((line): line is string => Boolean(line)),
  }));

  if (report.media.length === 0) return null;

  return (
    <>
      {visuais.length > 0 ? (
        <ul className="mt-3 grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">
          {visuais.map((item, position) => (
            <li key={item.id}>
              <MediaThumb
                item={item}
                onOpen={(retangulo) => {
                  setOrigem(retangulo);
                  lightbox.open(position);
                }}
                label={`Ver ${item.kind === 'video' ? 'vídeo' : 'foto'} ${position + 1} de ${visuais.length}`}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {/*
       * Um arquivo por linha (`<LinhaDeArquivo>`): o tipo, o nome e o tamanho,
       * e a linha inteira abre o arquivo — ou baixa, quando o navegador não o
       * mostra. Um filete claro separa um arquivo do outro. O download passa
       * pela API, que devolve o arquivo com o nome que ele tinha.
       */}
      {documents.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-px">
          {documents.map((document) => (
            <li key={document.id}>
              <LinhaDeArquivo
                obraId={report.obraId}
                documentId={document.id}
                originalName={document.originalName}
                sizeBytes={document.sizeBytes}
                url={document.url}
              />
            </li>
          ))}
        </ul>
      ) : null}

      <Lightbox
        items={slides}
        index={lightbox.index}
        onClose={lightbox.close}
        onNavigate={lightbox.navigate}
        origin={origem}
      />
    </>
  );
}

/**
 * A tarja do relatório removido.
 *
 * Quem removeu, quando e por quê — as três juntas, porque um registro riscado
 * sem motivo é pior que registro nenhum: quem lê meses depois não sabe se
 * aquilo foi engano de digitação ou se o fato nunca aconteceu.
 */
function TarjaDeRemocao({
  deletion,
  mediaCount,
}: {
  deletion: NonNullable<ReportSummary['deletion']>;
  mediaCount: number;
}) {
  return (
    <div className="mt-3 bg-danger-soft px-3.5 py-3 text-[13px] text-danger">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
        <Icons.Trash2 {...ICON} size={14} aria-hidden />
        Removido em {formatDate(deletion.at)}
        {deletion.by ? <span className="font-normal">por {deletion.by.name}</span> : null}
      </p>
      {deletion.reason ? (
        <p className="mt-1 leading-relaxed whitespace-pre-line">{deletion.reason}</p>
      ) : null}
      {/* Os anexos seguem o relatório: somem com ele e voltam se for restaurado. */}
      {mediaCount > 0 ? (
        <p className="mt-1 leading-relaxed">
          {mediaCount === 1 ? '1 anexo oculto' : `${mediaCount} anexos ocultos`} com o relatório.
          Voltam se ele for restaurado.
        </p>
      ) : null}
    </div>
  );
}

/**
 * A faixa da correção (ADR 060): quando, por quem e por quê — a última; as
 * anteriores ficam na auditoria. Em tinta neutra: corrigir é o registro ficar
 * certo, não um alarme.
 */
function TarjaDeCorrecao({ edit }: { edit: NonNullable<ReportSummary['edit']> }) {
  return (
    <div className="mt-3 border-l-2 border-line-2 bg-sunken px-3.5 py-2.5 text-[13px] text-ink-3">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Icons.PencilLine {...ICON} size={14} aria-hidden />
        <span className="font-medium text-ink">Corrigido em {formatDate(edit.at)}</span>
        {edit.by ? <span>por {edit.by.name}</span> : null}
      </p>
      {edit.reason ? (
        <p className="mt-1 leading-relaxed whitespace-pre-line">{edit.reason}</p>
      ) : null}
    </div>
  );
}

/** Ação do cartão do relatório: ícone quadrado, com o nome na dica e no leitor de tela. */
function AcaoDoRelatorio({
  rotulo,
  onClick,
  perigo = false,
  children,
}: {
  rotulo: string;
  onClick: () => void;
  perigo?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={rotulo}
      aria-label={rotulo}
      className={cn(
        'grid size-8 shrink-0 place-items-center text-ink-3 transition-colors hover:bg-sunken',
        perigo ? 'hover:text-danger' : 'hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}

/**
 * A faixa do autor: o marcador estendido para a direita, na chapa escura
 * (`plate`), da altura do quadrado do tipo e colada nele. Avatar e nome de quem
 * registrou, um filete, e a data da visita — antes soltos acima do cartão.
 *
 * Desliza para fora do quadrado ao chegar, logo depois que as pontas da moldura
 * aparecem: o recorte abre da esquerda para a direita, sem esticar o texto. Com
 * movimento reduzido, já vem aberta.
 */
function FaixaDoAutor({
  report,
  atraso,
  removido,
}: {
  report: ReportSummary;
  atraso: number;
  removido: boolean;
}) {
  const reduzir = useReducedMotion();

  return (
    <motion.span
      className={cn(
        'flex h-7 min-w-0 items-center bg-plate text-[13px] text-on-plate',
        removido && 'opacity-60',
      )}
      initial={reduzir ? false : { clipPath: 'inset(0 100% 0 0)' }}
      animate={{ clipPath: 'inset(0 0% 0 0)' }}
      transition={{ delay: atraso + 0.12, duration: 0.5, ease: EASE_OUT_QUINT }}
    >
      {/* O avatar ocupa a altura inteira da faixa, quadrado, como o quadrado do tipo. */}
      <Avatar name={report.author.name} url={report.author.avatarUrl} size={28} onDark quadrado />
      <span className="flex min-w-0 items-center px-3">
        <span className="sr-only">Registrado por</span>
        <span className="truncate font-medium">{report.author.name}</span>
      </span>
      <span aria-hidden className="h-full w-px shrink-0 bg-on-plate/15" />
      <span className="tabular flex shrink-0 items-center gap-1.5 px-3 text-on-plate/75">
        <Icons.CalendarDays {...ICON} size={13} aria-hidden />
        <span className="sr-only">, visita em</span>
        <span className={cn(removido && 'line-through')}>{formatDate(report.visitDate)}</span>
      </span>
    </motion.span>
  );
}

function ReportEntry({
  report,
  atraso,
  onEdit,
  onRemove,
  onRestore,
}: {
  report: ReportSummary;
  /** Segundos até a moldura do marcador começar a se desenhar. */
  atraso: number;
  onEdit?: ((report: ReportSummary) => void) | undefined;
  onRemove?: ((report: ReportSummary) => void) | undefined;
  onRestore?: ((report: ReportSummary) => void) | undefined;
}) {
  const removido = report.deletion !== null;
  // Corrige-se o publicado; o rascunho ainda nem entrou no histórico.
  const corrigivel = !removido && report.status === 'publicado';

  const selos =
    report.status === 'rascunho' || removido || report.edit || report.onBehalfOf ? (
      <>
        {report.status === 'rascunho' ? <Badge tone="muted">Rascunho</Badge> : null}
        {removido ? <Badge tone="danger">Removido</Badge> : null}
        {report.edit && !removido ? (
          <Badge tone="muted" icon={Icons.PencilLine}>
            Corrigido
          </Badge>
        ) : null}
        {report.onBehalfOf ? (
          <Badge tone="primary" icon={Icons.UserCog}>
            Em substituição a {report.onBehalfOf.name}
          </Badge>
        ) : null}
      </>
    ) : null;

  return (
    <li className="relative grid grid-cols-[36px_minmax(0,1fr)] gap-x-4">
      {/*
       * O cabeçalho: dentro da moldura de pontas, o quadrado do tipo e, colada
       * nele, a faixa escura com quem registrou e a data da visita — o
       * marcador se estende para dentro da coluna do conteúdo. Os selos vêm ao
       * lado da moldura, numa coluna própria que quebra linha sem passar por
       * baixo dela; no celular, onde não cabem ao lado, descem para cima do
       * cartão.
       */}
      <div className="col-span-2 flex items-start gap-3">
        <Moldura ritmo="curto" atraso={atraso} className="flex max-w-full min-w-0 p-1">
          <TypeMarker type={report.type} />
          <FaixaDoAutor report={report} atraso={atraso} removido={removido} />
        </Moldura>

        {selos ? (
          <span
            className={cn(
              'hidden min-w-0 flex-1 flex-wrap items-center gap-2 pt-[7px] sm:flex',
              removido && 'opacity-60',
            )}
          >
            {selos}
          </span>
        ) : null}
      </div>

      {/*
       * O fio desce do meio da borda de baixo da moldura, sob o quadrado, até o
       * pé do item — onde encontra a borda de cima da moldura seguinte. Liga
       * molduras, não quadrados. Preso à moldura (36px do alto, 18px da
       * esquerda: o meio do quadrado), e não à linha do cabeçalho: se os selos
       * quebrarem em duas linhas, o fio continua saindo da borda.
       */}
      <motion.span
        aria-hidden
        className={cn(FIO, 'absolute top-9 bottom-0 left-[18px] origin-top')}
        initial={{ scaleY: 0 }}
        animate={{ scaleY: 1 }}
        transition={{
          delay: atraso + fimDaMoldura('curto'),
          duration: FIO_DESCE,
          ease: EASE_OUT_QUINT,
        }}
      />

      {/*
        O removido fica em meio-tom: continua legível, mas não disputa a leitura
        com o que de fato vale no histórico da obra.
      */}
      <div className={cn('col-start-2 min-w-0 pt-2 pb-6', removido && 'opacity-60')}>
        {selos ? (
          <div className="mb-2 flex flex-wrap items-center gap-2 sm:hidden">{selos}</div>
        ) : null}
        <Card square className="p-4">
          <div className="flex items-start justify-between gap-3">
            <h3
              className={cn(
                'text-[15px] font-semibold tracking-tight',
                removido && 'line-through decoration-2',
              )}
            >
              {report.title}
            </h3>

            <div className="-m-1 flex shrink-0">
              {onEdit && corrigivel ? (
                <AcaoDoRelatorio rotulo={`Corrigir ${report.title}`} onClick={() => onEdit(report)}>
                  <Icons.Pencil {...ICON} size={16} aria-hidden />
                </AcaoDoRelatorio>
              ) : null}

              {onRemove && !removido ? (
                <AcaoDoRelatorio
                  rotulo={`Remover ${report.title}`}
                  onClick={() => onRemove(report)}
                  perigo
                >
                  <Icons.Trash2 {...ICON} size={16} aria-hidden />
                </AcaoDoRelatorio>
              ) : null}

              {onRestore && removido ? (
                <AcaoDoRelatorio
                  rotulo={`Restaurar ${report.title}`}
                  onClick={() => onRestore(report)}
                >
                  <Icons.ArchiveRestore {...ICON} size={16} aria-hidden />
                </AcaoDoRelatorio>
              ) : null}
            </div>
          </div>

          {report.description ? (
            <p
              className={cn(
                'mt-2 max-w-[68ch] leading-relaxed whitespace-pre-line text-ink-3',
                removido && 'line-through',
              )}
            >
              {report.description}
            </p>
          ) : null}

          {report.movement ? <MovementStrip movement={report.movement} /> : null}

          {report.edit && !removido ? <TarjaDeCorrecao edit={report.edit} /> : null}

          {report.deletion ? (
            <TarjaDeRemocao deletion={report.deletion} mediaCount={report.mediaCount} />
          ) : null}

          <ReportAttachments report={report} />
        </Card>
      </div>
    </li>
  );
}

/**
 * O pé da linha do tempo. A lista vem do mais novo para o mais antigo, então o
 * fim dela é o começo do histórico: o fio do último relatório desce até um
 * quadrado cheio, com "Início do acompanhamento" ao lado — em vez de parar no
 * meio do nada. Se a tela não trouxe todos (a lista carrega os 100 mais
 * recentes), o fio termina tracejado e diz quantos ficaram de fora: chamar de
 * início o que não é seria mentir sobre o histórico da obra.
 */
function InicioDoHistorico({ atraso, faltam }: { atraso: number; faltam: number }) {
  return (
    <motion.div
      className="flex gap-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: atraso, duration: 0.24, ease: EASE_OUT_QUINT }}
    >
      {faltam > 0 ? (
        <>
          <div className="flex w-9 shrink-0 justify-center">
            <span aria-hidden className="h-6 border-l border-dashed border-ink-3/50" />
          </div>
          <p className="self-center text-[12px] text-ink-3">
            {faltam === 1
              ? 'O relatório mais antigo não aparece aqui.'
              : `Os ${faltam} relatórios mais antigos não aparecem aqui.`}
          </p>
        </>
      ) : (
        <>
          <div aria-hidden className="flex w-9 shrink-0 flex-col items-center">
            <span className={cn(FIO, 'h-[5px]')} />
            <span className="size-[7px] bg-ink-3" />
          </div>
          <p className="text-[11px] leading-[17px] font-medium tracking-[0.06em] text-ink-3 uppercase">
            Início do acompanhamento
          </p>
        </>
      )}
    </motion.div>
  );
}

export function ReportTimeline({
  reports,
  total,
  loading,
  emptyAction,
  onEdit,
  onRemove,
  onRestore,
}: {
  reports: readonly ReportSummary[];
  /** Quantos relatórios a obra tem ao todo; maior que a lista, o começo não está na tela. */
  total?: number | undefined;
  loading: boolean;
  emptyAction?: React.ReactNode;
  /** Quando ausente, a linha do tempo não oferece a correção do publicado. */
  onEdit?: ((report: ReportSummary) => void) | undefined;
  /** Idem para a remoção. */
  onRemove?: ((report: ReportSummary) => void) | undefined;
  /** Idem para restaurar um relatório removido. */
  onRestore?: ((report: ReportSummary) => void) | undefined;
}) {
  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 3 }, (_, index) => (
          <div key={index} className="grid grid-cols-[36px_minmax(0,1fr)] gap-x-4 gap-y-2">
            <Skeleton className="col-span-2 h-9 w-72 max-w-full rounded-none" />
            <span />
            <Skeleton className="h-28 rounded-none" />
          </div>
        ))}
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <Card square>
        <EmptyState
          icon={FileText}
          title="Nenhum relatório registrado"
          description="Registre a primeira visita técnica para começar o acompanhamento desta obra."
          action={emptyAction}
        />
      </Card>
    );
  }

  return (
    <div>
      <ol>
        {reports.map((report, posicao) => (
          <ReportEntry
            key={report.id}
            report={report}
            atraso={atrasoDo(posicao)}
            onEdit={onEdit}
            onRemove={onRemove}
            onRestore={onRestore}
          />
        ))}
      </ol>
      <InicioDoHistorico
        atraso={atrasoDo(reports.length - 1) + fimDaMoldura('curto') + FIO_DESCE}
        faltam={Math.max((total ?? reports.length) - reports.length, 0)}
      />
    </div>
  );
}
